/**
 * Copyright © Veeam Software Group GmbH.
 */

import React, { useMemo } from 'react';
import { createLinkControl } from '@veeam/components';
import { capitalize } from '@veeam-vspc/shared/helpers';
import { colors as palette, CONTROL_SIZE, useGlobalLang, useGlobalServices } from '@veeam-vspc/shared/components';
import { observer } from 'mobx-react-lite';
import { ProductType } from '@veeam-vspc/models/web-controllers';
import { useLocation } from 'react-router-dom';

import type { VspcLang } from 'configs/languages';

import { BaseTrendChart } from '../../../BaseTrendChart';
import { StatefulCombobox } from 'views/components/StatefulCombobox';
import { ConsumedPointsContext } from './contexts/ConsumedPointsContext';
import { ConsumedPointsService } from './services/consumed-points.service';
import { ConsumedPointsStore } from './stores/ConsumedPointsStore';
import { useConsumedPointsStore } from './hooks/use-consumed-points-store';
import { useLocalStorageValue } from 'views/hooks';

import type { ComboboxItem } from 'core/interfaces';

export const ChartLayout = observer(() => {
    const consumedStore = useConsumedPointsStore();
    const lang = useGlobalLang<VspcLang>();
    const { transportService } = useGlobalServices();
    const consumedPointsService = useMemo(() => new ConsumedPointsService(transportService, lang), [transportService, lang]);

    const params = {
        colors: [palette.B400],
        yTitle: lang.POINTS,
    };

    const title = {
        text: capitalize(lang.CONSUMED_POINTS),
        tools: (
            <StatefulCombobox
                controlRenderer={createLinkControl()}
                onChange={consumedStore.setProductType}
                stateId={consumedStore.lsStateId}
                textGetter={(p: ComboboxItem) => p.text}
                value={consumedStore.productType}
                valueGetter={(p: ComboboxItem) => p.id}
                data={[
                    { id: ProductType.All, text: lang.ALL },
                    { id: ProductType.Vspc, text: lang.VEEAM_SERVICE_PROVIDER_CONSOLE },
                    { id: ProductType.CloudConnectServer, text: lang.VEEAM_CLOUD_CONNECT },
                    { id: ProductType.BackupServer, text: lang.VEEAM_BACKUP_AND_REPLICATION },
                    { id: ProductType.VOne, text: lang.VEEAM_ONE },
                    { id: ProductType.Vbo, text: lang.VEEAM_BACKUP_FOR_MICROSOFT },
                ]}
                size={CONTROL_SIZE.m}
            />
        ),
    };

    return (
        <BaseTrendChart
            objectsType={consumedStore.productType}
            params={params}
            service={consumedPointsService}
            title={title}
        />
    );
});

export const ConsumedPoints: React.FC = () => {
    const { pathname } = useLocation();
    const stateId = `${pathname}.ConsumedPoints.ProductType`;
    const initialType = useLocalStorageValue<ProductType>(stateId, ProductType.All);

    const consumedPointsStore = useMemo(() => new ConsumedPointsStore(initialType, stateId), [initialType, stateId]);
    return (
        <ConsumedPointsContext.Provider value={consumedPointsStore}>
            <ChartLayout />
        </ConsumedPointsContext.Provider>
    );
};
